import type { ContentActionMenuItem } from "@setplex/arcana"

import type { MediaTypes } from '@setplex/tria-api'
import type { ContentActionContentType } from '~/features/analytix/ui/models/content-actions'

import { IconNames } from "/app/libraries/arcana/src/atoms/Icon/index.ts"

import { MediaTypes as MediaTypesEnum } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { useCallback } from 'react'
import { model as i18nModel } from '~/core/i18n'
import { model as guestModel } from '~/features/subscriber/guest'
import { isContentWatchedEligible } from '../lib'
import * as contentWatchedModel from '../model'

type Params = {
  id?: number
  tvShowId?: number
  seasonId?: number | null
  isWithSeason?: boolean
  contentType?: MediaTypes | string | null
  name?: string
  analyticsContentType?: ContentActionContentType
  analyticsContentName?: string
  analyticsProgramName?: string
  analyticsRouteName?: string
  locked?: boolean
  blockedByAcl?: boolean
  hasPlaybackAccess?: boolean
  watched?: boolean | null
}

export const useContentWatchedAction = ({
  id,
  tvShowId,
  seasonId,
  isWithSeason,
  contentType,
  name,
  analyticsContentType,
  analyticsContentName,
  analyticsProgramName,
  analyticsRouteName,
  locked,
  blockedByAcl,
  hasPlaybackAccess,
  watched,
}: Params) => {
  const { setContentWatched, guest, i18n } = useUnit({
    setContentWatched: contentWatchedModel.setContentWatched,
    guest: guestModel.$guest,
    i18n: i18nModel.$t,
  })
  const { t } = i18n

  const watchedSelected = Boolean(watched)
  const eligible =
    Boolean(id) &&
    isContentWatchedEligible({
      contentType,
      guest: Boolean(guest),
      blockedByAcl: Boolean(blockedByAcl),
      locked: Boolean(locked),
      hasPlaybackAccess,
    })

  const handleWatchedSelect = useCallback(() => {
    if (!eligible || !id) return

    if (contentType === MediaTypesEnum.VOD) {
      setContentWatched({
        type: 'vod',
        id,
        watched: !watchedSelected,
        name,
        analyticsUiElementType: 'menu_option',
        analyticsContentType: analyticsContentType ?? 'vod',
        analyticsContentName: analyticsContentName ?? name,
        analyticsProgramName,
        analyticsRouteName,
      })
      return
    }

    if (contentType !== MediaTypesEnum.EPISODE || !tvShowId) return

    if (seasonId && isWithSeason !== false) {
      setContentWatched({
        type: 'episodeWithSeason',
        tvShowId,
        seasonId,
        episodeId: id,
        watched: !watchedSelected,
        name,
        analyticsUiElementType: 'menu_option',
        analyticsContentType: analyticsContentType ?? 'tvshow',
        analyticsContentName: analyticsContentName ?? name,
        analyticsProgramName,
        analyticsRouteName,
      })
      return
    }

    setContentWatched({
      type: 'episodeWithoutSeason',
      tvShowId,
      episodeId: id,
      watched: !watchedSelected,
      name,
      analyticsUiElementType: 'menu_option',
      analyticsContentType: analyticsContentType ?? 'tvshow',
      analyticsContentName: analyticsContentName ?? name,
      analyticsProgramName,
      analyticsRouteName,
    })
  }, [
    analyticsContentName,
    analyticsProgramName,
    analyticsContentType,
    analyticsRouteName,
    contentType,
    eligible,
    id,
    name,
    isWithSeason,
    seasonId,
    setContentWatched,
    tvShowId,
    watchedSelected,
  ])

  const menuItem: ContentActionMenuItem | null = eligible
    ? {
        id: 'watched',
        label: t(watchedSelected ? 'unmark_as_watched' : 'mark_as_watched'),
        icon: watchedSelected ? IconNames.eyeCrossed : IconNames.eyeOpen,
        onSelect: handleWatchedSelect,
      }
    : null

  return {
    eligible,
    handleWatchedSelect,
    menuItem,
    watchedSelected,
  }
}
